import { z } from "zod";

/**
 * The people's four lists, **as the doors send them** (`GENERIC-DOOR-SWEEP`, Arc 1, A48): `GET /users`, `GET /accounts`,
 * `GET /organisations` and `GET /organisations/{id}`.
 *
 * Until these files each reader declared the row it EXPECTED (the dealer's team, the expert's cabinet, the console's
 * three directories). The columns here are the wire's, spelled as the wire spells them: `GET /users` and the two
 * organisation doors send the row's SQL names (`full_name`, `invitation_id`), `GET /accounts` sends camelCase. That
 * inconsistency is the API's and is recorded, not repaired, by this contract; a rename is a different round with a
 * different cost. Strict: a column added or dropped on one side fails the integration spec that parses the real answer.
 */

/** One row of `GET /api/v1/users`, as `users.controller.ts` `list` selects it. */
export const teamMemberSchema = z.strictObject({
  id: z.string(),
  full_name: z.string(),
  email: z.string(),
  role: z.string(),
  status: z.string(),
  availability: z.string(),
  returns_on: z.string().nullable(),
  deactivated_on: z.string().nullable(),
  created_at: z.string(),
  created_by: z.string(),
  last_login_at: z.string().nullable(),
  /** The newest pending invitation for the address; `null` once accepted or revoked. */
  invitation_id: z.string().nullable(),
});
export const teamAnswerSchema = z.strictObject({ data: z.array(teamMemberSchema) });

/** One row of `GET /api/v1/accounts` (WF-CON-40's columns; the e-mail is deliberately absent). */
export const partnerAccountSchema = z.strictObject({
  userId: z.string(),
  fullName: z.string(),
  role: z.string(),
  status: z.string(),
  anonymised: z.boolean(),
  createdBy: z.string(),
  organisation: z.strictObject({
    id: z.string(),
    name: z.string(),
    type: z.string(),
    status: z.string(),
    memberCount: z.number().int().min(0),
  }),
});
export const accountsAnswerSchema = z.strictObject({ data: z.array(partnerAccountSchema) });

/** The organisation's administrator as the list derives it: four states, `null` when nobody was ever named. */
export const organisationAdministratorSchema = z.strictObject({
  fullName: z.string(),
  email: z.string(),
  invitationId: z.string().nullable(),
  state: z.enum(["active", "invitationPending", "invitationRevoked", "deactivated"]),
});

/** One row of `GET /api/v1/organisations`. */
export const organisationRowSchema = z.strictObject({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  status: z.string(),
  identity_verified: z.boolean(),
  convention_received: z.boolean(),
  opened_on: z.string().nullable(),
  created_at: z.string(),
  opened_by_name: z.string().nullable(),
  /** `count(*)::text`: a string on the wire. */
  member_count: z.string(),
  active_count: z.string(),
  invitation_pending_since: z.string().nullable(),
  administrator: organisationAdministratorSchema.nullable(),
  /** `FX-3`: whoever follows the partner (`partner_accounts.manager_user_id`), by name. */
  manager_name: z.string().nullable(),
});
export const organisationsAnswerSchema = z.strictObject({ data: z.array(organisationRowSchema) });

/** `GET /api/v1/organisations/{id}`: the organisation's columns and the accounts it holds. */
export const organisationAnswerSchema = z.strictObject({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  status: z.string(),
  identity_verified: z.boolean(),
  convention_received: z.boolean(),
  opened_on: z.string().nullable(),
  created_at: z.string(),
  accounts: z.array(
    z.strictObject({ id: z.string(), fullName: z.string(), role: z.string(), status: z.string() }),
  ),
});

export type TeamMember = z.infer<typeof teamMemberSchema>;
export type TeamAnswer = z.infer<typeof teamAnswerSchema>;
export type PartnerAccount = z.infer<typeof partnerAccountSchema>;
export type AccountsAnswer = z.infer<typeof accountsAnswerSchema>;
export type OrganisationRow = z.infer<typeof organisationRowSchema>;
export type OrganisationsAnswer = z.infer<typeof organisationsAnswerSchema>;
export type OrganisationAnswer = z.infer<typeof organisationAnswerSchema>;
